/**
 * Small, self-contained controls for the GEENA editorial header.
 * Uses native dialogs and is deliberately separate from Dawn's custom elements.
 */
(() => {
  class GeenaShell extends HTMLElement {
    connectedCallback() {
      this.activeTrigger = null;
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      this.querySelectorAll('[data-geena-drawer]').forEach((dialog) => {
        dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close(dialog);
        });
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) this.close(dialog);
        });
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
    }

    onClick(event) {
      const openButton = event.target.closest('[data-geena-open]');
      if (openButton && this.contains(openButton)) {
        const dialog = this.querySelector(`[data-geena-drawer="${openButton.dataset.geenaOpen}"]`);
        if (dialog) {
          event.preventDefault();
          this.open(dialog, openButton);
        }
        return;
      }

      const closeButton = event.target.closest('[data-geena-close]');
      if (closeButton && this.contains(closeButton)) {
        const dialog = closeButton.closest('[data-geena-drawer]');
        if (dialog) {
          event.preventDefault();
          this.close(dialog);
        }
        return;
      }

      const navigationLink = event.target.closest('[data-geena-close-on-select]');
      if (navigationLink && this.contains(navigationLink)) {
        const dialog = navigationLink.closest('[data-geena-drawer]');
        if (dialog) this.close(dialog, false);
      }
    }

    onKeydown(event) {
      const dialog = event.target.closest('[data-geena-drawer][open]');
      if (!dialog) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(dialog);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll(
        'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => !element.hidden && element.getClientRects().length);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open(dialog, trigger) {
      if (dialog.open) return;
      this.activeTrigger = trigger;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add('geena-dialog-open');
      const initialFocus = dialog.querySelector('[data-geena-initial-focus], input, button, a[href]');
      window.setTimeout(() => initialFocus && initialFocus.focus(), 0);
    }

    close(dialog, restoreFocus = true) {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      if (!this.querySelector('[data-geena-drawer][open]')) {
        document.documentElement.classList.remove('geena-dialog-open');
      }
      if (restoreFocus && this.activeTrigger) this.activeTrigger.focus();
      this.activeTrigger = null;
    }
  }

  if (!customElements.get('geena-shell')) {
    customElements.define('geena-shell', GeenaShell);
  }
})();
